import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import {
	FeatureOnboardingGroup,
	FeatureOnboardingStep,
} from "../../pages/FeatureOnboarding/schema/featureOnboarding";
import { featureOnboardingApi } from "./featureOnboarding.api";

export interface FeatureOnboardingState {
	currentGroup: FeatureOnboardingGroup | null;
	currentStepIndex: number;
}

const initialState: FeatureOnboardingState = {
	currentGroup: null,
	currentStepIndex: 0,
};

export const featureOnboardingSlice = createSlice({
	name: "featureOnboarding",
	initialState,
	reducers: {
		setCurrentStepIndex: (state, { payload }: PayloadAction<number>) => {
			state.currentStepIndex = payload;
		},
		resetOnboarding: (state) => {
			state.currentGroup = null;
			state.currentStepIndex = 0;
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			featureOnboardingApi.endpoints.getPendingOnboarding.matchFulfilled,
			(state, { payload }) => {
				const firstGroup = payload?.data?.[0] ?? null;

				state.currentGroup = firstGroup;
				state.currentStepIndex = 0;
			}
		);
		builder.addMatcher(featureOnboardingApi.endpoints.dismissStep.matchFulfilled, (state) => {
			// After dismissing a step, the cache invalidation will trigger a refetch.
			// Advance the step index optimistically so the UI feels instant.
			if (state.currentGroup && state.currentStepIndex < state.currentGroup.steps.length - 1) {
				state.currentStepIndex += 1;
			}
		});
		builder.addMatcher(featureOnboardingApi.endpoints.dismissAll.matchFulfilled, (state) => {
			state.currentGroup = null;
			state.currentStepIndex = 0;
		});
	},
});

export const { setCurrentStepIndex, resetOnboarding } = featureOnboardingSlice.actions;
export const featureOnboardingReducer = featureOnboardingSlice.reducer;
export const featureOnboardingSelector = (state: RootState) => state[featureOnboardingSlice.name];

// Derived selectors
export const selectCurrentStep = (state: RootState): FeatureOnboardingStep | null => {
	const { currentGroup, currentStepIndex } = featureOnboardingSelector(state);

	if (!currentGroup || currentGroup.steps.length === 0) {
		return null;
	}

	return currentGroup.steps[currentStepIndex] ?? null;
};

export const selectIsLastStep = (state: RootState): boolean => {
	const { currentGroup, currentStepIndex } = featureOnboardingSelector(state);

	if (!currentGroup) {
		return false;
	}

	return currentStepIndex === currentGroup.steps.length - 1;
};

export const selectStepProgress = createSelector(
	[
		(state: RootState) => featureOnboardingSelector(state).currentGroup,
		(state: RootState) => featureOnboardingSelector(state).currentStepIndex,
	],
	(currentGroup, currentStepIndex): { current: number; total: number } => {
		if (!currentGroup) {
			return { current: 0, total: 0 };
		}

		return { current: currentStepIndex + 1, total: currentGroup.steps.length };
	}
);
